import { getActiveProfileTable } from "building-generation/elements/profileTools";
import { ProfileExtrusionDescriptorType } from "building-types/profiles/profileTypesEnum";
import type { FasciaRail, FasciaTrimRail } from "building-types/roofs/roofTrimTypes";
import { worldInchesNew } from "common/utils/nominalNumbers";
import { polyLine3Length } from "geom/polyLine3";
import { queryProfileDescriptor } from "../queryProfileDescriptor";
import type { ExteriorTrimCollectionBuilding, TrimKeyMeasure } from "../trimTypes";

export function getFriezeBoardTrims(
  building: ExteriorTrimCollectionBuilding,
  trimKeyMeasures: TrimKeyMeasure[] = [],
): TrimKeyMeasure[] {
  if (!building) {
    return trimKeyMeasures;
  }

  const { roofSystems, settings, enabledFeatures, doc } = building;
  const { profile } = settings;
  const profileTable = getActiveProfileTable({
    settingsToCatalogs: enabledFeatures.settingsToCatalogs,
    profiles: doc.profiles,
    profileSettings: profile,
  });

  // iterate through roof systems
  for (const roofSystemId in roofSystems) {
    const { roofs } = roofSystems[roofSystemId];

    // iterate through roofs
    for (const roofId in roofs) {
      const roof = roofs[roofId];
      const { friezeBoardRails } = roof;

      trimKeyMeasures.push(
        ...friezeBoardRails.flatMap((friezeBoardRail) => {
          const descriptor = queryProfileDescriptor(
            profileTable,
            friezeBoardRail.friezeBoard,
            enabledFeatures.settingsToCatalogs,
          );
          if (!descriptor) {
            return [];
          }
          let height: number | undefined;
          let width: number | undefined;

          if (descriptor.profileType === ProfileExtrusionDescriptorType.BOARD) {
            height = descriptor.height;
            width = descriptor.width;
          }

          return [
            {
              category: "FRIEZE_BOARDS" as const,
              name: descriptor.name,
              profileId: friezeBoardRail.friezeBoard,
              length: worldInchesNew(polyLine3Length(friezeBoardRail.rail)),
              height: height ? worldInchesNew(height) : undefined,
              width: width ? worldInchesNew(width) : undefined,
              materialSlot: friezeBoardRail.material,
              rail: friezeBoardRail.rail,
              plane: friezeBoardRail.roofPerimeterRail?.edgePlane,
              handles: [{ type: "ROOF", id: roofId } as const],
            },
          ];
        }),
      );
    }
  }

  return trimKeyMeasures;
}

export function getFasciaTrims(
  building: ExteriorTrimCollectionBuilding,
  trimKeyMeasures: TrimKeyMeasure[] = [],
): TrimKeyMeasure[] {
  if (!building) {
    return trimKeyMeasures;
  }

  const { roofSystems } = building;

  const getFasciaName = ({ boardSize, boardThickness }: FasciaRail): string => {
    return `Fascia Board ${boardThickness} x ${boardSize}`;
  };

  // iterate through roof systems
  for (const roofSystemId in roofSystems) {
    const { roofs } = roofSystems[roofSystemId];

    // iterate through roofs
    for (const roofId in roofs) {
      const roof = roofs[roofId];
      const { fasciaRails } = roof;

      trimKeyMeasures.push(
        ...fasciaRails.map((fasciaRail) => ({
          category: "FASCIA_BOARDS" as const,
          name: getFasciaName(fasciaRail),
          length: worldInchesNew(polyLine3Length(fasciaRail.rail)),
          height: worldInchesNew(fasciaRail.boardSize),
          width: worldInchesNew(fasciaRail.boardThickness),
          materialSlot: fasciaRail.material,
          rail: fasciaRail.rail,
          plane: fasciaRail.roofPerimeterRail?.edgePlane,
          handles: [{ type: "ROOF", id: roofId } as const],
        })),
      );
    }
  }

  return trimKeyMeasures;
}

export function getFasciaTopTrims(
  building: ExteriorTrimCollectionBuilding,
  trimKeyMeasures: TrimKeyMeasure[] = [],
): TrimKeyMeasure[] {
  if (!building) {
    return trimKeyMeasures;
  }

  const { roofSystems } = building;

  const getFasciaTopTrimName = ({ boardSize, boardThickness }: FasciaTrimRail): string => {
    return `Fascia Top Trim ${boardThickness} x ${boardSize}`;
  };

  // iterate through roof systems
  for (const roofSystemId in roofSystems) {
    const { roofs } = roofSystems[roofSystemId];

    // iterate through roofs
    for (const roofId in roofs) {
      const roof = roofs[roofId];
      const { fasciaTopTrimRails } = roof;

      trimKeyMeasures.push(
        ...fasciaTopTrimRails.map((fasciaTopTrimRail) => ({
          category: "FASCIA_TOP_TRIMS" as const,
          name: getFasciaTopTrimName(fasciaTopTrimRail),
          length: worldInchesNew(polyLine3Length(fasciaTopTrimRail.rail)),
          height: worldInchesNew(fasciaTopTrimRail.boardSize),
          width: worldInchesNew(fasciaTopTrimRail.boardThickness),
          materialSlot: fasciaTopTrimRail.material,
          rail: fasciaTopTrimRail.rail,
          plane: fasciaTopTrimRail.roofPerimeterRail?.edgePlane,
          handles: [{ type: "ROOF", id: roofId } as const],
        })),
      );
    }
  }

  return trimKeyMeasures;
}

export function getBellyBandTrims(
  building: ExteriorTrimCollectionBuilding,
  trimKeyMeasures: TrimKeyMeasure[] = [],
): TrimKeyMeasure[] {
  if (!building) {
    return trimKeyMeasures;
  }

  const { roofSystems, settings, enabledFeatures, doc } = building;
  const { profile } = settings;
  const profileTable = getActiveProfileTable({
    settingsToCatalogs: enabledFeatures.settingsToCatalogs,
    profiles: doc.profiles,
    profileSettings: profile,
  });

  // iterate through roof systems
  for (const roofSystemId in roofSystems) {
    const { roofs } = roofSystems[roofSystemId];

    // iterate through roofs
    for (const roofId in roofs) {
      const roof = roofs[roofId];
      const { bellyBandBoardRails } = roof;

      for (const { rail, profileId, material } of bellyBandBoardRails) {
        const descriptor = queryProfileDescriptor(profileTable, profileId, enabledFeatures.settingsToCatalogs);
        if (!descriptor) {
          continue;
        }
        let height: number | undefined;
        let width: number | undefined;
        if (descriptor.profileType === ProfileExtrusionDescriptorType.BOARD) {
          height = descriptor.height;
          width = descriptor.width;
        }
        trimKeyMeasures.push({
          category: "BELLY_BANDS",
          name: descriptor.name,
          profileId,
          length: worldInchesNew(polyLine3Length(rail)),
          height: height ? worldInchesNew(height) : undefined,
          width: width ? worldInchesNew(width) : undefined,
          materialSlot: material,
          handles: [{ type: "ROOF", id: roofId }],
        });
      }
    }
  }

  return trimKeyMeasures;
}

export function getRoofTrims(
  building: ExteriorTrimCollectionBuilding,
  trimKeyMeasures: TrimKeyMeasure[] = [],
): TrimKeyMeasure[] {
  getFriezeBoardTrims(building, trimKeyMeasures);
  getBellyBandTrims(building, trimKeyMeasures);
  getFasciaTrims(building, trimKeyMeasures);
  getFasciaTopTrims(building, trimKeyMeasures);

  return trimKeyMeasures;
}
